import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";

type ActionRiskLevel = "low" | "medium" | "high";

type ModalConfirmOptions = {
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  riskLevel?: ActionRiskLevel;
  detail?: string;
  consequences?: string[];
  zoneLabel?: string;
  confirmMatch?: string;
  confirmMatchLabel?: string;
  confirmMatchPlaceholder?: string;
};

type ModalAlertOptions = {
  title?: string;
  confirmLabel?: string;
  variant?: "success" | "error" | "warning" | "info" | "question";
};

type ModalPromptOptions = {
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  placeholder?: string;
  value?: string;
  required?: boolean;
};

declare global {
  interface Window {
    WMModal?: {
      confirm: (message: string, options?: ModalConfirmOptions) => Promise<boolean>;
      alert: (message: string, options?: ModalAlertOptions) => Promise<void>;
      prompt: (message: string, options?: ModalPromptOptions) => Promise<string | null>;
      resolveConfirmOptions: (
        source?: HTMLElement | null,
        options?: ModalConfirmOptions,
        submitter?: HTMLElement | null,
        message?: string,
      ) => ModalConfirmOptions;
    };
  }
}

function cleanText(value: unknown): string {
  return String(value || "").trim();
}

function escapeHTML(value: unknown): string {
  return cleanText(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function cleanList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => cleanText(item)).filter(Boolean);
  }
  return cleanText(value)
    .split(/\s*\|\s*|\n+/)
    .map((item) => cleanText(item))
    .filter(Boolean);
}

function normalizeRiskLevel(value: unknown): ActionRiskLevel | "" {
  const normalized = cleanText(value).toLowerCase();
  if (normalized === "low" || normalized === "medium" || normalized === "high") {
    return normalized;
  }
  return "";
}

function parseBoolean(value: unknown): boolean | null {
  const normalized = cleanText(value).toLowerCase();
  if (!normalized) {
    return null;
  }
  if (normalized === "true" || normalized === "1" || normalized === "yes") {
    return true;
  }
  if (normalized === "false" || normalized === "0" || normalized === "no") {
    return false;
  }
  return null;
}

function inferRiskLevel(message: string, danger: boolean): ActionRiskLevel {
  const content = cleanText(message).toLowerCase();
  if (
    danger
    || /\b(delete|remove|revoke|overwrite|destroy|erase|purge)\b/.test(content)
    || /cannot be undone/.test(content)
  ) {
    return "high";
  }
  if (
    /\b(rotate|apply|replace|reset|publish|request change|send|dispatch|activate)\b/.test(content)
    || /old code will be deactivated/.test(content)
  ) {
    return "medium";
  }
  return "low";
}

function titleForRisk(riskLevel: ActionRiskLevel): string {
  if (riskLevel === "high") {
    return "Review destructive action";
  }
  if (riskLevel === "medium") {
    return "Review action";
  }
  return "Confirm action";
}

function defaultConfirmLabel(message: string, riskLevel: ActionRiskLevel): string {
  const normalized = cleanText(message).toLowerCase();
  if (normalized.startsWith("revoke")) {
    return "Revoke";
  }
  if (normalized.startsWith("delete") || normalized.startsWith("remove")) {
    return "Delete";
  }
  if (normalized.startsWith("apply")) {
    return "Apply";
  }
  if (riskLevel === "high") {
    return "Continue";
  }
  return "Confirm";
}

function popupClass(riskLevel: ActionRiskLevel, danger = false): string {
  const classes = ["wm-modal-popup", `wm-modal-popup-${riskLevel}`];
  if (danger || riskLevel === "high") {
    classes.push("wm-modal-popup-danger");
  }
  return classes.join(" ");
}

function confirmButtonClass(riskLevel: ActionRiskLevel, danger = false): string {
  if (danger || riskLevel === "high") {
    return "btn btn-danger wm-modal-button";
  }
  if (riskLevel === "medium") {
    return "btn btn-secondary wm-modal-button";
  }
  return "btn btn-primary wm-modal-button";
}

function baseOptions(title: string, riskLevel: ActionRiskLevel, danger = false) {
  return {
    title: cleanText(title) || "Please confirm",
    heightAuto: false,
    backdrop: true,
    allowEscapeKey: true,
    allowOutsideClick: riskLevel === "low",
    reverseButtons: riskLevel !== "low",
    buttonsStyling: false,
    customClass: {
      popup: popupClass(riskLevel, danger),
      title: "wm-modal-title",
      htmlContainer: "wm-modal-body",
      actions: "wm-modal-actions",
      confirmButton: confirmButtonClass(riskLevel, danger),
      cancelButton: "btn btn-secondary wm-modal-button",
      input: "form-input wm-modal-input",
    },
  } satisfies Partial<import("sweetalert2").SweetAlertOptions>;
}

function datasetValue(source: HTMLElement | null | undefined, key: string): string {
  if (!(source instanceof HTMLElement)) {
    return "";
  }
  return cleanText(source.dataset[key as keyof DOMStringMap]);
}

function mergeFirstNonEmpty(submitter: HTMLElement | null | undefined, source: HTMLElement | null | undefined, key: string): string {
  return datasetValue(submitter, key) || datasetValue(source, key);
}

function resolveConfirmOptions(
  source?: HTMLElement | null,
  options: ModalConfirmOptions = {},
  submitter?: HTMLElement | null,
  message?: string,
): ModalConfirmOptions {
  const rawMessage = cleanText(message)
    || mergeFirstNonEmpty(submitter, source, "confirm")
    || mergeFirstNonEmpty(submitter, source, "confirmClick");
  const title = mergeFirstNonEmpty(submitter, source, "confirmTitle") || cleanText(options.title);
  const detail = mergeFirstNonEmpty(submitter, source, "confirmDetail") || cleanText(options.detail);
  const confirmLabel = mergeFirstNonEmpty(submitter, source, "confirmConfirmLabel") || cleanText(options.confirmLabel);
  const cancelLabel = mergeFirstNonEmpty(submitter, source, "confirmCancelLabel") || cleanText(options.cancelLabel);
  const zoneLabel = mergeFirstNonEmpty(submitter, source, "confirmZone") || cleanText(options.zoneLabel);
  const confirmMatch = mergeFirstNonEmpty(submitter, source, "confirmMatch") || cleanText(options.confirmMatch);
  const confirmMatchLabel = mergeFirstNonEmpty(submitter, source, "confirmMatchLabel") || cleanText(options.confirmMatchLabel);
  const confirmMatchPlaceholder =
    mergeFirstNonEmpty(submitter, source, "confirmMatchPlaceholder") || cleanText(options.confirmMatchPlaceholder);
  const consequences = cleanList(
    mergeFirstNonEmpty(submitter, source, "confirmConsequences") || options.consequences,
  );
  const explicitDanger = parseBoolean(mergeFirstNonEmpty(submitter, source, "confirmDanger"));
  const sourceDanger = parseBoolean(options.danger);
  const danger = explicitDanger ?? sourceDanger ?? Boolean(
    submitter?.classList.contains("btn-danger")
      || source?.classList.contains("btn-danger")
      || source?.querySelector?.(".btn-danger"),
  );
  const explicitRisk = normalizeRiskLevel(mergeFirstNonEmpty(submitter, source, "confirmRisk")) || normalizeRiskLevel(options.riskLevel);
  const riskLevel = explicitRisk || inferRiskLevel(rawMessage, danger);

  return {
    ...options,
    title: title || titleForRisk(riskLevel),
    confirmLabel: confirmLabel || defaultConfirmLabel(rawMessage, riskLevel),
    cancelLabel: cancelLabel || "Cancel",
    danger: danger || riskLevel === "high",
    riskLevel,
    detail,
    consequences,
    zoneLabel,
    confirmMatch,
    confirmMatchLabel,
    confirmMatchPlaceholder,
  };
}

function buildConfirmHTML(message: string, options: ModalConfirmOptions): string {
  const riskLevel = normalizeRiskLevel(options.riskLevel) || "low";
  const consequences = cleanList(options.consequences);
  const zoneLabel = cleanText(options.zoneLabel);
  const detail = cleanText(options.detail);
  const confirmMatch = cleanText(options.confirmMatch);
  const confirmMatchLabel = cleanText(options.confirmMatchLabel)
    || (confirmMatch ? `Type ${confirmMatch} to continue.` : "");
  const parts: string[] = [
    `<div class="wm-modal-risk wm-modal-risk-${riskLevel}">`,
    `  <span class="wm-modal-risk-badge wm-modal-risk-badge-${riskLevel}">${escapeHTML(riskLevel)} risk</span>`,
  ];

  if (zoneLabel) {
    parts.push(`  <span class="wm-modal-zone-label">${escapeHTML(zoneLabel)}</span>`);
  }
  parts.push("</div>");
  parts.push(`<p class="wm-modal-message">${escapeHTML(message || "Are you sure?")}</p>`);

  if (detail) {
    parts.push(`<p class="wm-modal-detail">${escapeHTML(detail)}</p>`);
  }

  if (consequences.length > 0) {
    parts.push('<ul class="wm-modal-consequences">');
    consequences.forEach((item) => {
      parts.push(`<li>${escapeHTML(item)}</li>`);
    });
    parts.push("</ul>");
  }

  if (confirmMatchLabel) {
    parts.push(`<p class="wm-modal-match-help">${escapeHTML(confirmMatchLabel)}</p>`);
  }

  return parts.join("");
}

function syncConfirmMatch(matchText: string, input: HTMLInputElement | null, button: HTMLButtonElement | null): void {
  if (!input || !button) {
    return;
  }
  const matches = cleanText(input.value) === cleanText(matchText);
  button.disabled = !matches;
}

async function confirm(message: string, options: ModalConfirmOptions = {}): Promise<boolean> {
  const resolved = resolveConfirmOptions(null, options, null, message);
  const riskLevel = normalizeRiskLevel(resolved.riskLevel) || "low";
  const confirmMatch = cleanText(resolved.confirmMatch);
  const result = await Swal.fire({
    ...baseOptions(resolved.title || "Confirm action", riskLevel, !!resolved.danger),
    html: buildConfirmHTML(message, resolved),
    icon: riskLevel === "high" ? "warning" : "question",
    showCancelButton: true,
    input: confirmMatch ? "text" : undefined,
    inputPlaceholder: confirmMatch
      ? cleanText(resolved.confirmMatchPlaceholder) || `Type ${confirmMatch}`
      : undefined,
    confirmButtonText: cleanText(resolved.confirmLabel) || "Confirm",
    cancelButtonText: cleanText(resolved.cancelLabel) || "Cancel",
    focusCancel: riskLevel !== "low",
    didOpen: () => {
      if (!confirmMatch) {
        return;
      }
      const input = Swal.getInput() as HTMLInputElement | null;
      const button = Swal.getConfirmButton();
      syncConfirmMatch(confirmMatch, input, button);
      if (input) {
        input.addEventListener("input", () => syncConfirmMatch(confirmMatch, input, button));
      }
    },
    preConfirm: (value) => {
      if (confirmMatch && cleanText(value) !== confirmMatch) {
        Swal.showValidationMessage(`Type ${confirmMatch} to continue.`);
        return false;
      }
      return true;
    },
  });
  return !!result.isConfirmed;
}

async function alert(message: string, options: ModalAlertOptions = {}): Promise<void> {
  await Swal.fire({
    ...baseOptions(options.title || "Notice", "low"),
    text: cleanText(message),
    icon: options.variant || "info",
    confirmButtonText: cleanText(options.confirmLabel) || "OK",
  });
}

async function prompt(message: string, options: ModalPromptOptions = {}): Promise<string | null> {
  const result = await Swal.fire({
    ...baseOptions(options.title || "Enter a value", "low"),
    text: cleanText(message),
    icon: "question",
    input: "text",
    inputValue: cleanText(options.value),
    inputPlaceholder: cleanText(options.placeholder),
    inputAutoTrim: true,
    showCancelButton: true,
    confirmButtonText: cleanText(options.confirmLabel) || "Continue",
    cancelButtonText: cleanText(options.cancelLabel) || "Cancel",
    preConfirm: (value) => {
      const nextValue = cleanText(value);
      if (options.required && !nextValue) {
        Swal.showValidationMessage("This field is required.");
        return false;
      }
      return nextValue;
    },
  });
  if (!result.isConfirmed) {
    return null;
  }
  return cleanText(result.value);
}

if (!window.WMModal) {
  window.WMModal = {
    confirm,
    alert,
    prompt,
    resolveConfirmOptions,
  };
}
